import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { DynamicRule, RuleEntity, RuleOperator } from '@pdqdev/pdq-shared/dist/entities/Rules';
import { CartControlExtension, CheckoutExtensionUI } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { generateRandomString } from '@utils/string-utils';
import { CartControlExceptionRules, SubscriptionSelectorType, TRANSPARENT_APPEARANCE } from './config';
import {
  ExceptionFormGroup,
  ExceptionItemConfigurationsFormGroup,
  ExceptionSubscriptionSelectorFormGroup,
  TypedRuleGroup,
} from './components/cart-control-form/cart-control-form.component';

// PDQ-7681 — "Customers can:" defaults. Both chip-scaffolded and blank/hand-built exceptions start
// with every action unchecked; the merchant opts into what an exception allows.
const DEFAULT_ACTIONS: CartControlExtension.ExceptionActions = {
  changeVariant: false,
  editQuantity: false,
  removeItems: false,
  showSubscriptionSelector: false,
};

const DEFAULT_SUBSCRIPTION_SELECTOR: Required<CartControlExtension.SubscriptionSelector> = {
  type: SubscriptionSelectorType.DEFAULT_SELECTOR,
  upgradeButtonLabel: 'Upgrade to {subscription_group}',
  upgradeButtonAppearance: TRANSPARENT_APPEARANCE,
  upgradeButtonTextColor: CheckoutExtensionUI.Appearance.ACCENT,
};

function upgradeButtonLabelRequired(actions: AbstractControl): ValidationErrors | null {
  const { showSubscriptionSelector, subscriptionSelector } = actions.value as CartControlExtension.ExceptionActions;
  const isUpgradeSelected = showSubscriptionSelector && subscriptionSelector?.type === SubscriptionSelectorType.UPGRADE;

  if (isUpgradeSelected && !subscriptionSelector?.upgradeButtonLabel?.trim()) {
    return { upgradeButtonLabelRequired: true };
  }
  return null;
}

function createSubscriptionSelectorFormGroup(
  subscriptionSelector: Required<CartControlExtension.SubscriptionSelector>,
): FormGroup<ExceptionSubscriptionSelectorFormGroup> {
  return new FormGroup<ExceptionSubscriptionSelectorFormGroup>({
    type: new FormControl<SubscriptionSelectorType>(subscriptionSelector.type, { nonNullable: true }),
    upgradeButtonLabel: new FormControl<string>(subscriptionSelector.upgradeButtonLabel, { nonNullable: true }),
    upgradeButtonAppearance: new FormControl<string>(subscriptionSelector.upgradeButtonAppearance, {
      nonNullable: true,
    }),
    upgradeButtonTextColor: new FormControl<string>(subscriptionSelector.upgradeButtonTextColor, {
      nonNullable: true,
    }),
  });
}

function createExceptionActionsFormGroup(
  actions: CartControlExtension.ExceptionActions,
): FormGroup<ExceptionItemConfigurationsFormGroup> {
  return new FormGroup<ExceptionItemConfigurationsFormGroup>(
    {
      changeVariant: new FormControl<boolean>(actions.changeVariant, { nonNullable: true }),
      editQuantity: new FormControl<boolean>(actions.editQuantity, { nonNullable: true }),
      removeItems: new FormControl<boolean>(actions.removeItems, { nonNullable: true }),
      showSubscriptionSelector: new FormControl<boolean>(actions.showSubscriptionSelector ?? false, {
        nonNullable: true,
      }),
      subscriptionSelector: createSubscriptionSelectorFormGroup({
        ...DEFAULT_SUBSCRIPTION_SELECTOR,
        ...actions.subscriptionSelector,
      }),
    },
    { validators: upgradeButtonLabelRequired },
  );
}

// When a quick-add chip scaffolds an exception, pre-seed a single "Item source is <source>" condition.
function scaffoldConditionsForSource(source: CartControlExtension.ItemSource): DynamicRule[] {
  return [
    {
      entity: RuleEntity.ITEM_SOURCE,
      entityOperator: RuleOperator.IS_ANY,
      entityParams: { itemSourceValue: source },
      groupIndex: 0,
    },
  ];
}

// Builds one exception FormGroup. Pass `exception` to hydrate a persisted exception, or `source` to
// scaffold a new one from a quick-add chip. Shared by the container's initial form build and the
// exceptions UI's add/chip actions so both produce identical shapes.
export function createCartControlExceptionFormGroup(
  rulesDynamicFormService: RulesDynamicFormService,
  exception?: Partial<CartControlExtension.Exception>,
  source?: CartControlExtension.ItemSource,
): FormGroup<ExceptionFormGroup> {
  const conditions: DynamicRule[] =
    (exception?.conditions as DynamicRule[]) ?? (source ? scaffoldConditionsForSource(source) : []);

  // Persisted exception → its saved actions; a new exception (chip or blank) → all actions off.
  const actions: CartControlExtension.ExceptionActions = { ...DEFAULT_ACTIONS, ...exception?.actions };

  return new FormGroup<ExceptionFormGroup>({
    id: new FormControl<string>(exception?.id ?? 'exception_' + generateRandomString(12), { nonNullable: true }),
    source: new FormControl<string | null>(exception?.source ?? source ?? null),
    conditions: rulesDynamicFormService.initRulesFormArray(conditions, CartControlExceptionRules) as FormArray<
      FormGroup<TypedRuleGroup>
    >,
    actions: createExceptionActionsFormGroup(actions),
  });
}
